import { Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { confirmSignUp, resendSignUpCode } from '@aws-amplify/auth';
import { Router } from '@angular/router';
import { interval, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { UserQuery } from '../../store/user';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'rmb-email-verification',
  templateUrl: './email-verification.component.html',
  styleUrls: ['./email-verification.component.scss'],
  standalone: false,
})
export class EmailVerificationComponent implements OnInit {
  private router = inject(Router);
  private userQuery = inject(UserQuery);
  private translocoService = inject(TranslocoService);

  readonly TRANSLOCO_PREFIX = 'auth.verify.';
  registrationEmail: string;
  counter$: Observable<number>;
  codeForm: FormGroup = new FormGroup({
    code: new FormControl<string>('', [Validators.required]),
  });
  errorMessage: string;
  infoMsg: string;
  readonly emailVerified = signal(false);
  readonly loading = signal(false);

  async ngOnInit(): Promise<void> {
    this.registrationEmail = this.userQuery.getUserEmail();
    if (!this.registrationEmail) {
      console.error('Cannot Verify user without email.');
      await this.router.navigate(['/auth', 'login']);
    }
  }

  async resendCode(): Promise<void> {
    this.errorMessage = '';
    try {
      await resendSignUpCode({ username: this.registrationEmail });
      this.infoMsg = this.TRANSLOCO_PREFIX + 'resent';
      this.counter$ = this.getCounter(10);

      this.executeAfter(10000, () => {
        this.infoMsg = null;
      });
    } catch (err) {
      this.codeForm.get('code').setValue(undefined);
      this.errorMessage = this.translocoService.translate('auth.errors.' + err.name);
    }
  }

  async onCodeSubmit(): Promise<void> {
    if (this.codeForm.invalid) {
      return;
    }
    this.errorMessage = '';
    this.loading.set(true);

    try {
      await confirmSignUp({
        username: this.registrationEmail,
        confirmationCode: this.codeForm.value.code.trim(),
        options: {
          forceAliasCreation: true,
        },
      });
      this.infoMsg = this.TRANSLOCO_PREFIX + 'success';
      this.emailVerified.set(true);

      this.executeAfter(3000, async () => {
        await this.router.navigate(['/auth/login']);
      });
    } catch (err) {
      this.emailVerified.set(false);
      this.errorMessage = this.translocoService.translate('auth.errors.' + err.name);
    }
    this.loading.set(false);
  }

  private getCounter(count: number): Observable<number> {
    return interval(1000).pipe(
      map(() => (count = count - 1)),
      take(count)
    );
  }

  private executeAfter(time: number, callback: () => void | Promise<void>): void {
    interval(time).pipe(take(1)).subscribe(callback);
  }
}
